import { Fragment, useEffect, useState } from 'react'

import type { FanResponseDto } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import TextareaAutosize from 'react-textarea-autosize'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { classNames } from '@/utils/helpers.ts'

import CustomNotePresetIcon from '../../../icons/add-custom-note-preset.svg'
import DescriptionIcon from '../../../icons/description.svg'
import EditIcon from '../../../icons/edit.svg'
import { IconButton } from '../../components/icon-button'
import { isPresetInserted } from './utils'

interface Props {
  fanId: FanResponseDto['id']
  notice: string
  onUpdate: (notice: string) => void
  isUpdating?: boolean
  onOpenDialog?: (noticeToEdit: string) => void
  isEditAvailable?: boolean
  preset?: string
}

const schema = z.object({
  notice: z.string(),
})

type FormValues = z.infer<typeof schema>
const FORM_ID = 'edit-notice-inline'

export const Notice = ({
  fanId,
  notice,
  onUpdate,
  isUpdating = false,
  onOpenDialog,
  isEditAvailable = true,
  preset = '',
}: Props) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      notice: notice ?? undefined,
    },
  })
  const [isInlineEditModeActive, setIsInlineEditModeActive] = useState(false)

  useEffect(() => {
    if (fanId) {
      setIsInlineEditModeActive(false)
    }
  }, [fanId])

  const resetInlineEdit = () => {
    setIsInlineEditModeActive(false)
    form.reset()
  }

  const noticeToEdit = notice || preset

  const startInlineEdit = () => {
    if (!isEditAvailable) {
      return
    }

    form.setValue('notice', noticeToEdit)
    setIsInlineEditModeActive(true)
  }

  const onSaveInlineEdit = ({ notice }: FormValues) => {
    onUpdate(notice)
    setIsInlineEditModeActive(false)
  }

  const handleClickInsertPreset = () => {
    const newNotice = (notice ? notice + '\n' : '') + preset

    onUpdate(newNotice)
  }

  const handleClickEditNotice = () => {
    if (onOpenDialog) {
      onOpenDialog(noticeToEdit)
    } else {
      startInlineEdit()
    }
  }

  const isPresetButtonDisabled = isPresetInserted(notice, preset) || isUpdating

  return (
    <div className="space-y-4 rounded-md bg-card p-3">
      <div className="flex items-center justify-between gap-2 rounded-md bg-secondary-50 p-2 text-title dark:bg-secondary-600">
        <DescriptionIcon />
        <p className="flex-1 text-sm font-bold">Notes</p>
        {isInlineEditModeActive ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className="h-max p-0 text-title"
              onClick={resetInlineEdit}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <div className="h-5 border-l border-border-secondary" />
            <Button
              variant="ghost"
              className="h-max p-0"
              type="submit"
              form={FORM_ID}
              disabled={isUpdating}
            >
              Save
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            {!!preset && (
              <IconButton
                onClick={handleClickInsertPreset}
                icon={CustomNotePresetIcon}
                disabled={isPresetButtonDisabled}
                className="text-primary-500"
                tooltip="Add custom note preset"
              />
            )}

            {isEditAvailable && (
              <IconButton
                onClick={handleClickEditNotice}
                icon={EditIcon}
                disabled={isUpdating}
                className="text-primary-500"
              />
            )}
          </div>
        )}
      </div>
      {isInlineEditModeActive ? (
        <form
          id={FORM_ID}
          onSubmit={form.handleSubmit(onSaveInlineEdit)}
        >
          <TextareaAutosize
            {...form.register('notice')}
            autoFocus
            className="w-full rounded-md border border-border-secondary bg-card px-3 py-4 outline-none"
            minRows={3}
          />
        </form>
      ) : (
        <div
          className={classNames('break-words', {
            'cursor-pointer': isEditAvailable,
          })}
          onClick={startInlineEdit}
        >
          {notice
            ? notice?.split('\n')?.map((part, i) =>
                part.length ? (
                  <Fragment key={i}>
                    {part}
                    <br />
                  </Fragment>
                ) : (
                  <br key={i} />
                ),
              )
            : 'No notes'}
        </div>
      )}
    </div>
  )
}
